import { Children, ReactElement, ReactNode } from "react";
import { Tab as TabTitle, TabGroup, TabList, TabPanel, TabPanelProps, TabPanels } from "@headlessui/react";

import { Sizes } from "styles/sizes";

import { cx } from "utils";


type TabsSizes = Required<Pick<Sizes<{ title: string }>, "SM" | "MD" | "LG">>;

const tabsSizes: TabsSizes = {
  SM: {
    title: "text-sm h-10",
  },
  MD: {
    title: "text-sm h-12"
  },
  LG: {
    title: "text-base h-14"
  }
};

type TabAlignment = "start" | "center" | "end";

export interface TabProps extends Pick<TabPanelProps, "unmount" | "children"> {
  title: string;
}

export function Tab(_props: TabProps) {
  return <></>;
}

function Title(
  {
    align,
    children,
  }: {
    align: TabAlignment;
    children: ReactNode;
  }
) {
  return (
    <TabTitle
      as="div"
      className={ cx(
        "h-full flex flex-1 items-center cursor-pointer text-neutral-900 data-[selected]:text-primary-600 focus:outline-none border-b-2 border-b-transparent data-[selected]:border-b-primary-600",
        align === "start" ? "justify-start" : (align === "end" ? "justify-end" : "justify-center")
        )
      }
    >
      { children }
    </TabTitle>
  );
}

interface TabsProps {
  size?: keyof typeof tabsSizes;
  align?: TabAlignment;
  children: ReactElement<TabProps> | ReactElement<TabProps>[];
  className?: string;
  containerClassName?: string;
  selectedIndex?: number;
  onChange?: (index: number) => void;
}

export default function Tabs(
  {
    size = "MD",
    align = "start",
    children,
    className = "",
    containerClassName,
    selectedIndex,
    onChange
  }: TabsProps
) {
  const childrenArray = Children.toArray(children) as ReactElement<TabProps>[];
  const hasTabContent = childrenArray.find(tabProps => tabProps.props.children !== undefined) !== undefined;

  return (
    <TabGroup
      className={ cx("flex flex-col gap-y-4 h-full border-b-[1px] border-b-neutral-200", containerClassName) }
      selectedIndex={selectedIndex}
      onChange={onChange}
    >
      <TabList
        className={ cx(
          "flex flex-row items-center w-min text-nowrap gap-x-8",
          tabsSizes[size].title,
          className
        ) }
      >
        { childrenArray.map((tab, idx) =>
          <Title key={ idx } align={ align }>{ tab.props.title }</Title>)
        }
      </TabList>

      { hasTabContent && (
        <TabPanels className="flex-grow">
          { childrenArray.map((tab, idx) =>
            <TabPanel
              className="size-full"
              key={ idx }
              unmount={ tab.props.unmount }
            >
              { tab.props.children }
            </TabPanel>
          ) }
        </TabPanels>
      ) }
    </TabGroup>
  );
};
